import React, { useEffect, useMemo, useRef, useState } from 'react';
import CollectionKeys from 'hooks/molecules/collection-keys';
import * as _ from 'lodash';
import Grid from '@material-ui/core/Grid';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Select, MenuItem, FormControl, InputLabel } from '@material-ui/core';
import { useItems } from 'hooks/atoms/items-hook';
import { uploadTemplate } from '../../../../http/upload-assets-client';
import { useStateObject } from '../../../../hooks/utils';
import AppButtonMain from '../../../basic/AppButtonMain';
import { useFindUniqueCTX } from '../../../../hooks/token-manager';
import axiosClient from '../../../../http/axios-client';
import { useAlerts } from '../../../../hooks/alerts-hooks';

const TemplateUpload = (
  {
    trait,
  },
) => {
  const formRef = useRef();

  const [ selectedFile, setSelectedFile ] = useState(null);

  // eslint-disable-next-line no-unused-vars
  const [ localeStr, setLocaleStr ] = useState(2);
  const [ region, setRegion ] = useState('EU');

  // context should always have the trait set, since templates are created per trait
  const [ context, setContextProperty ] = useStateObject({
    trait,
    category: null,
    locale: null,
    race: null,
  });

  useEffect(() => {
    if (_.isEqual(context.trait, trait)) {
      return;
    }
    setContextProperty('trait', trait);
  }, [ trait, setContextProperty ]);

  const {
    // eslint-disable-next-line no-unused-vars
    items: locales,
    itemsLoading: localesLoading,
  } = useItems(CollectionKeys.LOCALES);

  const {
    ctx,
    ctxIsLoading,
  } = useFindUniqueCTX(Object.keys(context).reduce((acc, k) => {
    acc[k] = context[k]?.id || null;
    return acc;
  }, {}));

  const {
    data: template,
    isLoading: templateIsLoading,
    isIdle: templateIsIdle,
  } = useQuery(
    [ 'templates', { ctx: ctx?.id || null }, localeStr, region ],
    () => {
      return axiosClient.get(`templates?locale=${ localeStr }&country_region=${ region }`, {
        params: {
          context: ctx?.id,
          _sort: `${ 'published_at' }:DESC`,
        },
      }).then((data) => {
        if (!data || !data.length) {
          return null;
        }
        return data[0];
      });
    },
    {
      enabled: !!ctx?.id,
      onError() {
        alertError('Something went wrong. Please try again.');
      },
    },
  );

  const { alertSuccess, alertError } = useAlerts();

  const queryClient = useQueryClient();

  const {
    mutateAsync: createTemplate,
    isLoading: templateIsCreating,
  } = useMutation(
    [ 'templates', 'create' ],
    ({
      formData,
      context: context_,
      locale,
    }) => {
      return Promise.resolve()
        .then(() => {
          return uploadTemplate(formData, locale);
        })
        .then((data) => {
          if (!data || !data.id) {
            throw new Error('Failed to fetch correct template data');
          }
          return axiosClient.put(`templates/${ data.id }`, {
            context: context_.id,
            locale: localeStr,
            country_region: region,
          });
        });
    }, {
      onSuccess() {
        setSelectedFile(null);
        alertSuccess('Template was uploaded successfully');
      },
      onError() {
        alertError('Something went wrong. Please try again.');
      },
      onSettled() {
        return queryClient.invalidateQueries([ 'templates' ]);
      },
    },
  );

  const uploadIsDisabled = useMemo(() => {
    return !ctx?.id || ctxIsLoading || !selectedFile || templateIsCreating || localesLoading;
  }, [ ctx, ctxIsLoading, selectedFile, templateIsCreating || localesLoading ]);

  const {
    mutateAsync: deleteTemplate,
    isLoading: templateIsDeleting,
  } = useMutation(
    [ 'templates', 'delete' ],
    (t) => {
      return axiosClient.delete(`templates/${ t.id }`);
    },
    {
      onSettled() {
        return queryClient.invalidateQueries([ 'templates' ]);
      },
    },
  );

  return (
    <Grid container>
      {
        (templateIsLoading || templateIsIdle || localesLoading) ? <CircularProgress /> : (
          <>
            <Grid item xs={ 12 } style={ { margin: '12px 0px' } }>
              {
                !template?.id ? (
                  <>
                    <form ref={ formRef }>
                      <input
                        type='file'
                        name='file'
                        accept='.doc,.docx'
                        onChange={ (e) => {
                          return setSelectedFile(e.target.value);
                        } }
                      />
                    </form>

                    <br />

                    <FormControl>
                      <InputLabel>Locale</InputLabel>
                      <Select
                        label='Locale'
                        variant='standard'
                        value={ localeStr }
                        onChange={ (e) => {
                          setLocaleStr(e.target.value);
                        } }
                      >
                        {
                          locales?.map((locale) => {
                            return (
                              <MenuItem
                                key={ locale.id }
                                value={ locale.id }
                              >
                                {locale.name}
                              </MenuItem>
                            );
                          })
                        }
                      </Select>
                    </FormControl>

                    <FormControl style={ { marginLeft: '50px' } }>
                      <InputLabel>Region</InputLabel>
                      <Select
                        label='Locale'
                        variant='standard'
                        value={ region }
                        onChange={ (e) => {
                          setRegion(e.target.value);
                        } }
                      >
                        <MenuItem
                          value='EU'
                        >
                          EU
                        </MenuItem>

                        <MenuItem
                          value='US'
                        >
                          US
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </>
                ) : (
                  <>
                    <p>
                      Uploaded template:
                      {template.location}
                    </p>

                    <FormControl>
                      <InputLabel>Locale</InputLabel>
                      <Select
                        variant='standard'
                        label='Locale'
                        value={ localeStr }
                        onChange={ (e) => {
                          setLocaleStr(e.target.value);
                        } }
                      >
                        {
                          locales?.map((locale) => {
                            return (
                              <MenuItem
                                key={ locale.id }
                                value={ locale.id }
                              >
                                {locale.name}
                              </MenuItem>
                            );
                          })
                        }
                      </Select>
                    </FormControl>

                    <FormControl style={ { marginLeft: '50px' } }>
                      <InputLabel>Region</InputLabel>
                      <Select
                        label='Locale'
                        variant='standard'
                        value={ region }
                        onChange={ (e) => {
                          setRegion(e.target.value);
                        } }
                      >
                        <MenuItem
                          value='EU'
                        >
                          EU
                        </MenuItem>

                        <MenuItem
                          value='US'
                        >
                          US
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </>
                )
              }
            </Grid>
            <Grid item xs={ 12 } style={ { margin: '12px 0px' } }>
              {
                !template?.id ? (
                  <AppButtonMain
                    text={ templateIsCreating ? 'Uploading' : 'Upload' }
                    disabled={ uploadIsDisabled }
                    onClick={ () => {
                      const formData = new FormData(formRef.current);
                      return createTemplate({ formData, context: ctx, localeStr });
                    } }
                  />
                ) : (
                  <AppButtonMain
                    color='#ae0b0b'
                    text={ templateIsDeleting ? 'Deleting' : 'Delete' }
                    disabled={ templateIsDeleting || !template?.id }
                    onClick={ () => { return deleteTemplate(template); } }
                  />
                )
              }
            </Grid>
          </>
        )
      }
    </Grid>
  );
};

export default TemplateUpload;
